import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import './Admin.css';

export const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [preAuthToken, setPreAuthToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const result = await adminService.login(username, password);
      if (result.requires2FA) {
        setPreAuthToken(result.preAuthToken);
        setShowTwoFactor(true);
      } else {
        navigate('/admin');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await adminService.verify2FA(twoFactorCode, preAuthToken);
      navigate('/admin');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  if (showTwoFactor) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="admin-login-icon">🔐</div>
          <h2>Verify Identity</h2>
          <p className="admin-subtitle">Enter the code sent to the admin email</p>

          {error && <div className="admin-error">{error}</div>}

          <form onSubmit={handleVerify2FA}>
            <div className="admin-form-group">
              <label htmlFor="twoFactorCode">Verification Code</label>
              <input
                type="text"
                id="twoFactorCode"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                style={{ textAlign: 'center', letterSpacing: '0.4em', fontSize: '1.4rem' }}
                autoComplete="one-time-code"
                autoFocus
              />
            </div>

            <button type="submit" className="admin-btn-primary" disabled={isLoading || twoFactorCode.length !== 6}>
              {isLoading ? 'Verifying...' : 'Verify & Enter'}
            </button>
          </form>

          <button
            onClick={() => { setShowTwoFactor(false); setError(null); setTwoFactorCode(''); }}
            style={{ marginTop: '1rem', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.875rem' }}
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-login-container">
      <div className="admin-login-card">
        <div className="admin-login-icon">&#x1F512;</div>
        <h2>Admin Portal</h2>
        <p className="admin-subtitle">Restricted access</p>

        {error && <div className="admin-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Admin username"
              autoComplete="off"
              required
            />
          </div>

          <div className="admin-form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              required
            />
          </div>

          <button type="submit" className="admin-btn-primary" disabled={isLoading}>
            {isLoading ? 'Authenticating...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};
